import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useBackClose } from "../../hooks/useBackClose";
import {
  Menu,
  X as XIcon,
  Search,
  MoreHorizontal,
  ChevronDown,
  KeyRound,
  LogOut,
  Download,
  Share,
  Plus,
  User as UserIcon,
  LogIn,
  UserPlus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguages, useMenus } from "../../hooks/useBusinessData";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { requireLogin, isLoggedIn as isLoggedInFn } from "../../hooks/useAuth";
import { unregisterFcmToken, registerFcmToken } from "../../firebase";
import { useNotification } from "../../contexts/NotificationContext";
import NotificationDropdown from "../ui/NotificationDropdown";
import { useBannerInquiryUnread } from "../../contexts/BannerInquiryUnreadContext";
import SearchSuggestPanel from "../ui/SearchSuggestPanel";
import { addRecentSearch } from "../../utils/recentSearch";
import { loadSearchIndex, searchBanners } from "../../utils/searchEngine";
import { markIgnoreUntil } from "../../utils/clickGuard";
import { _forceRefresh } from "../../App";
// 모든 모달 팝업은 지연 로딩(코드 스플리팅) — 클릭 시에만 로드 → 초기 번들에서 제외 → 첫 실행 단축
const BannerCollectionPopup = lazy(() => import("../ui/BannerCollectionPopup"));
const BannerFavoritesPopup = lazy(() => import("../ui/BannerFavoritesPopup"));
const MobileWidgetsPopup = lazy(() => import("../ui/MobileWidgetsPopup"));
const LoginPopup = lazy(() => import("../ui/LoginPopup"));
const SignupPopup = lazy(() => import("../ui/SignupPopup"));
const NoticePopup = lazy(() => import("../ui/NoticePopup"));
const AdInquiryPopup = lazy(() => import("../ui/AdInquiryPopup"));
const UserBoardPopup = lazy(() => import("../ui/UserBoardPopup"));
const HelpPopup = lazy(() => import("../ui/HelpPopup"));
const TravelInfoPopup = lazy(() => import("../ui/TravelInfoPopup"));
const ServiceCenterPopup = lazy(() => import("../ui/ServiceCenterPopup"));
const ChangePasswordPopup = lazy(() => import("../ui/ChangePasswordPopup"));
const ProfilePopup = lazy(() => import("../ui/ProfilePopup"));
const MyBannerInquiriesPage = lazy(() => import("../../pages/MyBannerInquiriesPage"));

type PopupKey = "event" | "adInquiry" | "notice" | "favorites" | "travelInfo" | "serviceCenter" | "customerInquiry" | "help" | "login" | "signup" | "changePassword" | "profile" | "improvementRequest" | "bugReport" | "myBannerInquiries" | null;

export default function Header() {
  const { t, i18n } = useTranslation();
  const { unreadCount } = useNotification();
  const { total: inquiryUnread } = useBannerInquiryUnread();
  // 종 뱃지 — 좌측 게시글(FCM 알림) 수 / 우측 댓글(1:1 비공개 문의) 수, 분리 표시
  const languages = useLanguages();
  const [isLoggedIn, setIsLoggedIn] = useState(isLoggedInFn());
  const [loggedInNick, setLoggedInNick] = useState(localStorage.getItem("nick_name") || "");
  // 광고주(is_advertiser) 이거나 role >= 80 관리자이면 '내 배너 문의' 메뉴 노출
  const [isAdvertiser, setIsAdvertiser] = useState(false);
  useEffect(() => {
    if (!isLoggedIn) { setIsAdvertiser(false); return; }
    const token = localStorage.getItem("access_token");
    if (!token) return;
    fetch(`${API_BASE}/user/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        const me = d?.data ?? d;
        setIsAdvertiser(!!me?.is_advertiser || (Number(me?.role) || 0) >= 80);
      })
      .catch(() => setIsAdvertiser(false));
  }, [isLoggedIn]);

  // 개선요청/오류보고 미읽음 댓글(관리자 답변) 수 — 헤더 메뉴 뱃지용.
  // 미읽음 = comment_count - max(로컬 읽음수, 서버 read cursor 기준 읽음수). 서버 값 덕분에
  // 다른 브라우저/기기에서 읽은 것도 반영됨(#532 — UserBoardPopup.tsx 목록 배지와 동일 패턴을
  // 여기서도 적용. 이 메뉴 뱃지가 로컬스토리지만 보고 있어서 #532 신고의 실제 증상이던
  // "이 브라우저에서 쓴 내 댓글이 다른 브라우저에선 안읽음으로 뜸" 현상이 여기 남아있었음).
  const [ubUnread, setUbUnread] = useState<{ IMPROVEMENT_REQUEST: number; BUG_REPORT: number }>({ IMPROVEMENT_REQUEST: 0, BUG_REPORT: 0 });
  useEffect(() => {
    if (!isLoggedIn) { setUbUnread({ IMPROVEMENT_REQUEST: 0, BUG_REPORT: 0 }); return; }
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const calc = async () => {
      const types = ["IMPROVEMENT_REQUEST", "BUG_REPORT"] as const;
      const next = { IMPROVEMENT_REQUEST: 0, BUG_REPORT: 0 };
      await Promise.all(types.map(async (bt) => {
        try {
          const r = await fetch(`${API_BASE}/user/boards/?board_type=${bt}&size=100`, { headers: { Authorization: `Bearer ${token}` } });
          const d = await r.json();
          const items = d?.data?.items || [];
          let unread = 0;
          for (const p of items) {
            let localRead = 0;
            try { localRead = parseInt(localStorage.getItem(`ub_cmt_read_${bt}_${p.id}`) || "0", 10) || 0; } catch { /* ignore */ }
            const commentCount = p.comment_count || 0;
            const serverRead = Math.max(0, commentCount - (p.unread_count ?? commentCount));
            const readCount = Math.max(localRead, serverRead);
            unread += Math.max(0, commentCount - readCount);
          }
          next[bt] = unread;
        } catch { /* silent */ }
      }));
      setUbUnread(next);
    };
    calc();
    const onChanged = () => calc();
    window.addEventListener("userboard-read-changed", onChanged);
    window.addEventListener("focus", onChanged);
    return () => { window.removeEventListener("userboard-read-changed", onChanged); window.removeEventListener("focus", onChanged); };
  }, [isLoggedIn]);
  const ubBadgeCount = (url?: string | null) =>
    url === "improvementRequest" ? ubUnread.IMPROVEMENT_REQUEST : url === "bugReport" ? ubUnread.BUG_REPORT : 0;

  // 공지사항 미읽음 — 오류신고와 동일 패턴(REST 조회 + 로컬 읽음 + focus/이벤트 갱신).
  //  미읽음 = 마지막으로 읽은 공지 id(notice_last_read_id) 보다 새 공지 수.
  const [noticeUnread, setNoticeUnread] = useState(0);
  useEffect(() => {
    const calc = async () => {
      try {
        const r = await fetch(`${API_BASE}/public/business/notices?size=50`);
        const data = await r.json();
        const items: Array<{ id: number }> = Array.isArray(data) ? data : [];
        let lastRead = 0;
        try { lastRead = parseInt(localStorage.getItem("notice_last_read_id") || "0", 10) || 0; } catch { /* ignore */ }
        setNoticeUnread(items.filter((n) => (n.id || 0) > lastRead).length);
      } catch { /* silent */ }
    };
    calc();
    const onChanged = () => calc();
    window.addEventListener("notice-read-changed", onChanged);
    window.addEventListener("focus", onChanged);
    return () => { window.removeEventListener("notice-read-changed", onChanged); window.removeEventListener("focus", onChanged); };
  }, []);

  // 메뉴 항목별 뱃지 수 (개선요청/오류신고 + 공지사항)
  const menuBadgeCount = (url?: string | null) =>
    url === "notice" ? noticeUnread : ubBadgeCount(url);

  // 로그인 상태 자동 동기화 — 다른 탭/storage 변경, 백그라운드→포그라운드 전환,
  // 글로벌 fetch 인터셉터의 refresh 성공 후 dispatch 되는 "auth-changed" CustomEvent
  useEffect(() => {
    const sync = () => {
      setIsLoggedIn(isLoggedInFn());
      setLoggedInNick(localStorage.getItem("nick_name") || "");
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") sync();
    };
    window.addEventListener("storage", sync);
    window.addEventListener("auth-changed", sync);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("auth-changed", sync);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  const [notifOpen, setNotifOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [langClosing, setLangClosing] = useState(false);
  const [activePopup, setActivePopup] = useState<PopupKey>(null);
  // 로고 클릭(강력 새로고침) — SW 해제+캐시 삭제 하는 동안 잠깐 무반응처럼 보이는 문제 →
  // 스피너로 진행 중임을 표시 (App.tsx 의 업데이트 모달과 동일한 목적).
  const [logoRefreshing, setLogoRefreshing] = useState(false);
  // ProfilePopup 진입 시 광고주 섹션 강조 여부 (광고문의 게이트로 열린 경우 true)
  const [profileHighlightAdvertiser, setProfileHighlightAdvertiser] = useState(false);
  // 시스템 알림 클릭 → 로그인 필요 팝업이었는데 비로그인이라 LoginPopup 으로 유도한 경우,
  // 로그인 성공 시 원래 열려던 팝업(+게시글 id)으로 자동 복귀시키기 위한 대기값.
  const pendingNotifTargetRef = useRef<{ key: PopupKey; targetId?: number } | null>(null);
  // 개선요청/오류보고 팝업이 특정 게시글로 자동 진입해야 할 때(알림 클릭) 그 게시글 id.
  const [autoOpenBoardItemId, setAutoOpenBoardItemId] = useState<number | undefined>(undefined);

  // 광고문의 클릭 — 광고주만 광고문의 진입, 일반회원/대기는 프로필로 유도
  const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";
  const handleAdInquiryOrProfile = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setActivePopup("login");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/user/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const me = data?.data ?? data;
      if (me?.is_advertiser === true) {
        setProfileHighlightAdvertiser(false);
        setActivePopup("adInquiry");
      } else {
        setProfileHighlightAdvertiser(true);
        setActivePopup("profile");
      }
    } catch {
      // me 조회 실패 시엔 안전하게 광고문의 그대로 진입 (채팅 영역에서 다시 분기)
      setActivePopup("adInquiry");
    }
  };

  // MainMenu 의 모바일 사용자영역에서 popup 호출 — CustomEvent 통신
  useEffect(() => {
    const handler = (e: Event) => {
      const key = (e as CustomEvent).detail as PopupKey;
      if (key) setActivePopup(key);
    };
    window.addEventListener("open-popup", handler);
    return () => window.removeEventListener("open-popup", handler);
  }, []);

  // 전역 로그인 요청 이벤트 — 모든 페이지/팝업(베너 클릭 등)에서 호출
  // HomePage 가 마운트되어 있을 때는 HomePage 리스너도 동일하게 처리하므로
  // 우리는 HomePage가 없는 경우(타 페이지에서 모아보기 팝업 등)에만 fallback 으로 열림
  useEffect(() => {
    const handler = () => {
      // 이미 LoginPopup 이 열려 있으면 중복 오픈 방지
      setActivePopup((cur) => (cur === "login" ? cur : "login"));
    };
    window.addEventListener("require-login", handler);
    return () => window.removeEventListener("require-login", handler);
  }, []);
  // 검색바 focus 시 추천/최근 검색어 패널 표시 (요구사항 #246)
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [widgetsOpen, setWidgetsOpen] = useState(false);  // 모바일 ⋯ → PC 우측 사이드바 위젯 팝업
  const [widgetsAnchor, setWidgetsAnchor] = useState<{ top: number; right: number }>({ top: 56, right: 8 });
  const performSearch = (kw: string) => {
    const q = (kw || "").trim();
    if (!q) return;
    addRecentSearch(q);
    setSearchQuery(q);
    setSuggestOpen(false);
    navigate(`/?q=${encodeURIComponent(q)}`);
  };
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [urlParams, setUrlParams] = useSearchParams();
  const clearSearch = () => {
    setSearchQuery("");
    const next = new URLSearchParams(urlParams);
    next.delete("q");
    setUrlParams(next, { replace: true });
  };
  const [searchQuery, setSearchQuery] = useState(urlParams.get("q") || "");
  // 실시간 검색건수 (X 좌측 숫자 표시) — 클라 인덱스 기반, 서버요청 0
  const [matchCount, setMatchCount] = useState<number | null>(null);
  // 선택 국가 — HomePage 와 동일 키(localStorage favorite_country_id, 기본 1). 검색을 이 국가로 스코프
  const selectedCountryId = parseInt(localStorage.getItem("favorite_country_id") || "1") || 1;
  useEffect(() => { loadSearchIndex(i18n.language).catch(() => {}); }, [i18n.language]);
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || q.startsWith("#")) { setMatchCount(null); return; }
    let alive = true;
    loadSearchIndex(i18n.language).then((idx) => { if (alive) setMatchCount(searchBanners(q, idx, 99999, selectedCountryId).length); });
    return () => { alive = false; };
  }, [searchQuery, i18n.language, selectedCountryId]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // 요구사항 #550: 안드로이드 뒤로가기로 모바일 햄버거 메뉴 닫기 (pushState 방식)
  useBackClose(() => setMobileMenuOpen(false), mobileMenuOpen);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 56, left: 8 });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const langRefMobile = useRef<HTMLDivElement>(null);
  const langRefDesktop = useRef<HTMLDivElement>(null);
  const LANG_EN: Record<string, string> = { ko: "Korean", en: "English", fil: "Filipino", vi: "Vietnamese", km: "Khmer" };
  const currentLang = languages.find((l) => l.key === i18n.language) || { key: "ko", label: "Korean", flag: "🇰🇷", alpha2: "ko", alpha3: "kor" };

  const changeLang = (key: string) => {
    i18n.changeLanguage(key);
    localStorage.setItem("lang", key);
  };

  const closeLang = () => {
    setLangClosing(true);
    setTimeout(() => {
      setLangOpen(false);
      setLangClosing(false);
    }, 350);
  };

  // 요구사항 #550(C): 안드로이드 뒤로가기로 Header 드롭다운(사용자메뉴/언어/알림) 닫기 (pushState 방식)
  useBackClose(() => setUserMenuOpen(false), userMenuOpen);
  // 언어는 350ms 페이드(closeLang)를 태우지 않고 즉시 닫아 다른 미니팝업과 반응 통일
  useBackClose(() => { setLangClosing(false); setLangOpen(false); }, langOpen);
  useBackClose(() => setNotifOpen(false), notifOpen);

  useEffect(() => {
    // 요구사항 #281 댓글 #445: 번역/햄버거 등 모든 드롭다운에서 외부 클릭 시
    // 모바일 ghost click 으로 배너가 열리는 결함. markIgnoreUntil + touchstart 추가.
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      let closed = false;
      // 모바일/데스크탑 두 컨테이너 중 어느 쪽에도 포함되지 않을 때만 외부 클릭으로 판정
      const inMobile = !!langRefMobile.current?.contains(target);
      const inDesktop = !!langRefDesktop.current?.contains(target);
      if (!inMobile && !inDesktop) {
        if (langOpen && !langClosing) { closeLang(); closed = true; }
      }
      const el = e.target as Element;
      if (!el.closest?.("[data-user-menu]") && userMenuOpen) {
        setUserMenuOpen(false);
        closed = true;
      }
      if (closed) markIgnoreUntil(500);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (langOpen && !langClosing) closeLang();
      if (userMenuOpen) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [langOpen, langClosing, userMenuOpen]);

  // PWA 설치 (Dock/홈화면 바로가기)
  const { canPrompt: canInstallPwa, isStandalone, isIOS, install: installPwa } = usePwaInstall();
  const [showPwaHelp, setShowPwaHelp] = useState(false);
  const pwaHelpRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(pwaHelpRef, showPwaHelp);
  const handlePwaInstall = async () => {
    if (canInstallPwa) {
      await installPwa();
    } else {
      setShowPwaHelp(true);
    }
  };

  const handleLogout = async () => {
    await unregisterFcmToken();
    try {
      const token = localStorage.getItem("access_token");
      if (token) {
        await fetch(`${process.env.REACT_APP_API_URL || "https://api.trippang.com"}/user/session/signout`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` },
        });
      }
    } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("uid");
    localStorage.removeItem("nick_name");
    setIsLoggedIn(false);
    setLoggedInNick("");
    setUserMenuOpen(false);
    window.dispatchEvent(new CustomEvent("auth-changed"));
  };

  // DB 기반 — admin/menus 에서 관리. icon_url/popup_key/route_path/requires_login 모두 DB.
  const menuItems = useMenus();

  // 시스템 푸시 알림(open_popup 계열) 오픈 공통 로직 — 최초 URL 진입(콜드스타트)과
  // 아래 SW postMessage(앱이 이미 열려있던 경우) 양쪽에서 재사용.
  // 로그인 필요 팝업(개선요청/오류보고/광고문의 등)인데 비로그인이면 LoginPopup 으로 먼저 유도하고,
  // 로그인 성공 후 원래 팝업(+target_id 게시글)으로 자동 복귀시킨다 — requires_login 은 하드코딩하지
  // 않고 menuItems(DB, useMenus())에서 그대로 조회해 dispatchMenuClick 과 동일한 판정 기준을 쓴다.
  const openNotifPopupTarget = (key: PopupKey, targetId?: number) => {
    if (!key) return;
    const openTarget = () => {
      setActivePopup(key);
      if (targetId && (key === "improvementRequest" || key === "bugReport")) {
        setAutoOpenBoardItemId(targetId);
      }
    };
    const requiresLogin = menuItems.find((m) => m.url === key)?.requires_login;
    if (requiresLogin) {
      requireLogin(openTarget, () => {
        pendingNotifTargetRef.current = { key, targetId };
        setActivePopup("login");
      });
    } else {
      openTarget();
    }
  };

  // 시스템 푸시 알림 클릭(앱 완전종료 상태) → SW 가 "/?open_popup=<key>[&target_id=<id>]" 로
  // 새로 열어서 진입 — 최초 로드 시 쿼리스트링을 읽어 해당 팝업 자동 오픈.
  const openPopupHandledRef = useRef(false);
  useEffect(() => {
    if (openPopupHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const key = params.get("open_popup") as PopupKey;
    if (!key) { openPopupHandledRef.current = true; return; }
    // menuItems 가 아직 로딩 전이면(빈 배열) requires_login 판정을 내릴 수 없으므로 대기
    if (menuItems.length === 0) return;
    openPopupHandledRef.current = true;

    const targetIdRaw = params.get("target_id");
    const targetId = targetIdRaw ? Number(targetIdRaw) || undefined : undefined;
    params.delete("open_popup");
    params.delete("target_id");
    const rest = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));

    openNotifPopupTarget(key, targetId);
  }, [menuItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // 시스템 푸시 알림 클릭(앱이 이미 열려있던 경우) — SW 가 탭 전체를 client.navigate() 로
  // 리로드시키면 index.html 의 #boot 스플래시가 매번 다시 뜨는 문제가 있어, SW 쪽은 전체
  // 리로드 대신 postMessage 로 목적지만 알려주고 여기서 react-router 내부 이동으로 처리한다
  // (리로드 없음 → 스플래시 재노출 없음). 베너류는 /banner/:id 라우트, 나머지는 팝업 오픈.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg || msg.type !== "SW_NOTIFICATION_NAVIGATE" || !msg.url) return;
      let url: URL;
      try { url = new URL(msg.url, window.location.origin); } catch { return; }
      if (url.pathname.startsWith("/banner/")) {
        navigate(url.pathname + url.search, { state: { backgroundLocation: routerLocation } });
        return;
      }
      const key = url.searchParams.get("open_popup") as PopupKey;
      const targetIdRaw = url.searchParams.get("target_id");
      const targetId = targetIdRaw ? Number(targetIdRaw) || undefined : undefined;
      openNotifPopupTarget(key, targetId);
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [menuItems, routerLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // 메뉴 클릭 시 디스패치 — url 자동 분기:
  //  - "/" 로 시작 → React Router navigate (별도 페이지 이동)
  //  - 그 외       → PopupKey 로 모달 표시 (Header.tsx activePopup state)
  // 'adInquiry' 는 광고주 인증 분기, requires_login 은 로그인 게이트 적용
  const dispatchMenuClick = (m: { url?: string | null; requires_login: boolean }) => {
    const target = (m.url || "").trim();
    if (!target) return;

    // 라우트 이동 모드
    if (target.startsWith("/")) {
      const go = () => navigate(target);
      if (m.requires_login) {
        requireLogin(go, () => setActivePopup("login"));
      } else {
        go();
      }
      return;
    }

    // PopupKey 모드
    const pk = target as PopupKey;
    if (pk === "adInquiry") {
      handleAdInquiryOrProfile();
      return;
    }
    if (m.requires_login) {
      requireLogin(
        () => setActivePopup(pk),
        () => setActivePopup("login"),
      );
    } else {
      setActivePopup(pk);
    }
  };

  return (
    <>
    {/* TRIPPANG 로고 바
        요구사항 #293: 모바일 좌단=햄버거, 우단=알림/닉네임/언어 (햄버거 ↔ 언어 swap)
        비로그인 시 회원가입/로그인은 햄버거 메뉴 내부로 이동 (#4) */}
    <div id="title-bar" className="bg-white py-1 md:py-4">
      {/* 모바일: 3칼럼 grid — col1==col3(1fr 등분)이라 col2 워드마크가 화면 정중앙. 구분선 없음. 데스크탑: flex */}
      <div className="mx-auto grid grid-cols-[1fr_auto_1fr] md:flex w-full max-w-[var(--content-width)] items-center px-[3.75px] sm:px-[7.5px] relative">
        {/* col1: 햄버거 (요구사항 #293) — col3 와 동일 너비, 좌측 정렬 */}
        <div className="flex md:hidden items-center justify-self-start">
          <button
            ref={hamburgerRef}
            onClick={() => {
              // 비로그인이어도 로그인 팝업을 띄우지 않고 메뉴를 연다 (메뉴 안에 로그인/회원가입 표시)
              if (!mobileMenuOpen && hamburgerRef.current) {
                const r = hamburgerRef.current.getBoundingClientRect();
                setMenuPos({ top: r.bottom + 4, left: r.left });  // 햄버거 바로 아래로 펼침
              }
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            className="flex shrink-0 items-center justify-center h-9 w-9 text-slate-700 hover:text-sky-600 transition-colors"
            aria-label="menu"
          >
            {mobileMenuOpen ? <XIcon className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* col2: TRIPPANG 로고 — 나머지 너비 차지, 워드마크 중앙. 클릭 시 강력 새로고침 */}
        <div className="flex justify-center md:flex-1">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // 강력 새로고침 — SW unregister + caches 삭제 + reload (App._forceRefresh).
              // 요구사항: 로고 클릭은 메인 진입 + 강력 새로고침. 인트로는 다시 재생하지 않음.
              try { window.sessionStorage.setItem("intro-shown", "1"); } catch { /* private mode */ }
              // 현재 path 가 / 가 아니면 reload 후 로고 의도와 다른 곳에 갈 수 있어 / 로 명시 이동.
              if (window.location.pathname !== "/") {
                window.location.href = window.location.origin + "/";
                return;
              }
              setLogoRefreshing(true);
              _forceRefresh();
            }}
            className="flex items-center gap-1.5 md:gap-2 text-[15px] md:text-4xl font-black text-sky-600 tracking-wider min-h-[44px] px-2 md:px-0 cursor-pointer select-none"
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "rgba(2,132,199,0.15)" }}
          >
            <div className="flex h-[18px] w-[18px] md:h-10 md:w-10 items-center justify-center rounded md:rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 text-[7px] md:text-sm font-black text-white">
              TP
            </div>
            TRIPPANG
          </a>
        </div>

        {/* 강력 새로고침 진행 중 오버레이 — 로고 박스 안 작은 아이콘 교체는 모바일에서 잘 안 보여서
            어드민(Sidebar.tsx/AdminLayout.tsx)과 동일한 화면 전체 오버레이 스피너로 통일. */}
        {logoRefreshing && createPortal(
          <div
            className="fixed top-0 left-0 z-[9999] flex items-center justify-center bg-slate-950/80"
            style={{ width: "100vw", height: "100dvh" }}
          >
            <div className="h-10 w-10 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
          </div>,
          document.body
        )}

        {/* col3: (로그아웃) 로그인·회원가입·국기 / (로그인) 종·프로필·국기 — 우측 정렬. 간격 1.33px */}
        <div className="flex md:hidden items-center gap-[1.33px] justify-self-end">
          {!isLoggedIn && (
            <>
              {/* 로그인 */}
              <button
                onClick={() => setActivePopup("login")}
                className="flex h-6 w-6 items-center justify-center text-slate-700 hover:text-sky-600 transition-colors"
                aria-label={t("header.login")}
              >
                <LogIn className="h-[18px] w-[18px]" />
              </button>
              {/* 회원가입 */}
              <button
                onClick={() => setActivePopup("signup")}
                className="flex h-6 w-6 items-center justify-center text-slate-700 hover:text-sky-600 transition-colors"
                aria-label={t("header.signup")}
              >
                <UserPlus className="h-[18px] w-[18px]" />
              </button>
            </>
          )}
          {isLoggedIn && (
            <>
              {/* 알림 종 */}
              <button
                data-notif-bell
                onClick={() => { if (typeof Notification !== "undefined" && Notification.permission !== "granted") { window.dispatchEvent(new Event("open-notif-permission")); return; } setNotifOpen(p => !p); if (typeof Notification !== "undefined" && Notification.permission === "granted") registerFcmToken(); }}
                className="relative flex h-6 w-6 items-center justify-center text-[10.5px]"
                aria-label="notifications"
              >
                🔔
                {/* 좌측: 게시글(알림) 수, 우측: 댓글(1:1문의) 수 — 합산 대신 분리 표시 */}
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -left-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-blue-500 px-1 text-[9px] font-bold text-white" title={t("notif.new_posts")}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
                {inquiryUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-red-400 px-1 text-[9px] font-bold text-white" title={t("notif.new_comments")}>
                    {inquiryUnread > 99 ? "99+" : inquiryUnread}
                  </span>
                )}
              </button>
              {/* 프로필 아이콘 + 드롭다운 (닉네임 제거, #428 후속) */}
              <div data-user-menu className="relative">
                <button
                  onClick={() => setUserMenuOpen((p) => !p)}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-amber-600 transition-all hover:bg-slate-100"
                  aria-label={loggedInNick}
                >
                  <UserIcon className="h-[18px] w-[18px]" />
                </button>
                {userMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 z-[60] w-44 overflow-hidden rounded-xl border border-white/10 bg-slate-900 shadow-xl"
                    style={{ transformOrigin: "top", animation: "dropdownOpen 0.25s ease-out forwards" }}
                  >
                    <button
                      onClick={() => { setUserMenuOpen(false); setActivePopup("profile"); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <UserIcon className="h-4 w-4 text-slate-400" />
                      {t("header.profile")}
                    </button>
                    <div className="border-t border-white/5" />
                    <button
                      onClick={() => { setUserMenuOpen(false); setActivePopup("changePassword"); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <KeyRound className="h-4 w-4 text-slate-400" />
                      {t("header.changePassword")}
                    </button>
                    <div className="border-t border-white/5" />
                    <button
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-sky-400 transition-colors hover:bg-white/5 hover:text-sky-300"
                    >
                      <LogOut className="h-4 w-4 text-sky-400" />
                      {t("header.logout")}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {/* 언어선택 — 우측 끝 (국기만 표시, 언어코드 제거, #428 후속) */}
          <div ref={langRefMobile} className="shrink-0 relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex h-6 w-6 items-center justify-center text-slate-700 hover:text-sky-600 transition-colors"
              aria-label="language"
            >
              <span className="text-base leading-none">{currentLang.flag}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-1 z-[100] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                {languages.map((l) => (
                  <button
                    key={l.key}
                    onClick={() => { changeLang(l.key); setLangOpen(false); }}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors ${
                      i18n.language === l.key ? "bg-sky-50 text-sky-600" : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span>{l.flag}</span>
                    <span>{LANG_EN[l.key] || l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* 검색 바 — 요구사항 #254 #3: 모바일은 화면 너비 가득 (좌우 여백 0)
        시안 #237 적용: 모바일 하늘색 테두리 + placeholder 제거
        언어선택은 title-bar 좌측으로 이동됨 (#254 #1) */}
    <div className="bg-white pb-1 md:pb-4">
      <div className="mx-auto flex max-w-[var(--content-width)] items-center justify-center gap-2 px-[3.75px] md:px-[7.5px]">
        {/* 데스크탑: 가운데 정렬 검색바 + focus 시 추천/최근 패널 */}
        <div className="hidden md:flex w-full max-w-md relative">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              readOnly={!isLoggedIn}
              onChange={(e) => { setSearchQuery(e.target.value); setSuggestOpen(true); }}
              onFocus={(e) => {
                if (!isLoggedIn) { e.currentTarget.blur(); setActivePopup("login"); return; }
                setSuggestOpen(true);
              }}
              onClick={() => {
                if (!isLoggedIn) { setActivePopup("login"); return; }
                setSuggestOpen(true);
              }}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
              onKeyDown={(e) => { if (e.key === "Enter" && searchQuery.trim()) { performSearch(searchQuery); } else if (e.key === "Escape") { e.preventDefault(); setSuggestOpen(false); } }}
              placeholder={t("header.search")}
              className="w-full rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 pl-4 pr-14 py-1 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-sky-500"
            />
            {searchQuery && matchCount !== null && (
              <span className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-900 tabular-nums">{matchCount}</span>
            )}
            {searchQuery && (
              <button
                onMouseDown={(e) => { e.preventDefault(); clearSearch(); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full text-sky-600 hover:bg-sky-100 transition-colors"
                aria-label="clear"
              >
                <XIcon className="h-4 w-4" strokeWidth={3} />
              </button>
            )}
          </div>
          <button
            onClick={() => performSearch(searchQuery)}
            className="flex items-center rounded-r-lg border border-slate-300 bg-slate-100 px-3 py-1 text-slate-600 hover:bg-slate-200 transition-colors"
          >
            <Search className="h-4 w-4" />
          </button>
          {suggestOpen && <SearchSuggestPanel onSelect={performSearch} onClose={() => setSuggestOpen(false)} query={searchQuery} countryId={selectedCountryId} />}
        </div>

        {/* 모바일: 하늘색 테두리, placeholder 없음, 화면 너비 가득 (#254 #3) */}
        <div className="flex md:hidden w-full items-center">
          <div className="flex flex-1 min-w-0 relative">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={searchQuery}
                readOnly={!isLoggedIn}
                onChange={(e) => { setSearchQuery(e.target.value); setSuggestOpen(true); }}
                onFocus={(e) => {
                  if (!isLoggedIn) { e.currentTarget.blur(); setActivePopup("login"); return; }
                  setSuggestOpen(true);
                }}
                onClick={() => {
                  if (!isLoggedIn) { setActivePopup("login"); return; }
                  setSuggestOpen(true);
                }}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                onKeyDown={(e) => { if (e.key === "Enter" && searchQuery.trim()) { performSearch(searchQuery); } else if (e.key === "Escape") { e.preventDefault(); setSuggestOpen(false); } }}
                placeholder=""
                className="w-full min-w-0 rounded-l-lg border-y border-l border-sky-400 bg-white pl-2.5 pr-14 py-1 text-sm text-slate-900 outline-none focus:border-sky-500"
              />
              {searchQuery && matchCount !== null && (
                <span className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-900 tabular-nums">{matchCount}</span>
              )}
              {searchQuery && (
                <button
                  onMouseDown={(e) => { e.preventDefault(); clearSearch(); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full text-sky-600 hover:bg-sky-100 transition-colors"
                  aria-label="clear"
                >
                  <XIcon className="h-4 w-4" strokeWidth={3} />
                </button>
              )}
            </div>
            <button
              onClick={() => performSearch(searchQuery)}
              className="shrink-0 flex items-center rounded-r-lg border border-sky-400 bg-slate-100 px-3 py-1 text-sky-600 hover:bg-slate-200 transition-colors"
              aria-label="search"
            >
              <Search className="h-4 w-4" />
            </button>
            {suggestOpen && <SearchSuggestPanel onSelect={performSearch} onClose={() => setSuggestOpen(false)} query={searchQuery} countryId={selectedCountryId} />}
          </div>
          {/* 모바일 전용: PC 우측 사이드바(날씨/환율/코인) 위젯 팝업 */}
          <button
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              // top 을 검색바(=⋯ 버튼) 상단에 일치, 높이는 화면 하단까지 (MobileWidgetsPopup 이 bottom:0)
              setWidgetsAnchor({ top: r.top, right: 0 });
              setWidgetsOpen(true);
            }}
            className="shrink-0 ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            aria-label="widgets"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>

    {widgetsOpen && <Suspense fallback={null}><MobileWidgetsPopup onClose={() => setWidgetsOpen(false)} top={widgetsAnchor.top} right={widgetsAnchor.right} /></Suspense>}

    {/* 요구사항 #466 후속: 핀치 줌 중 버벅임 — backdrop-blur 제거는 유지(이미 98% 불투명이라
        블러 효과 자체가 육안으로 거의 안 보임 + 리페인트 비용만 큼).
        will-change/translateZ 강제 레이어 승격은 되돌림 — position:sticky 요소에 붙이면
        WebKit에서 sticky 재계산 타이밍과 충돌해 오히려 더 끊기는 경우가 있음(사용자 확인) */}
    <header className="sticky top-0 z-50 bg-white/98">
      {/* 모바일: 사용자영역/언어/PWA 모두 title-bar/검색바 로 이동됨 — 데스크탑은 기존 h-10 유지 */}
      <div className="relative mx-auto flex h-0 md:h-10 w-full max-w-[var(--content-width)] items-center md:justify-between text-slate-900 px-2 md:px-4">

        {/* 모바일: 이 영역은 빈 공간 (위로 통합) — sticky 동작만 유지 */}
        {/* 모바일 햄버거: title-bar 좌단, 회원가입/로그인: title-bar 우단, 검색/언어: 검색바, 새로고침: MainMenu 우단 */}

        {/* 데스크탑: 메뉴 — flex-1 으로 우측 영역과 명확히 분리, overflow-x-auto 로 너무 길면 스크롤
            #239 fix: 영어/필리핀어 모드에서 메뉴와 우측 (Login/SignUp/언어) 겹침 해소 */}
        <div className="hidden md:flex flex-1 min-w-0 items-center justify-center gap-0.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {menuItems.map((item) => {
            const ub = menuBadgeCount(item.url);
            return (
            <button
              key={item.key}
              onClick={() => dispatchMenuClick(item)}
              className="glow-yellow shrink-0 px-2 font-semibold whitespace-nowrap inline-flex items-center gap-1"
            >
              {item.label}
              {ub > 0 && (
                <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white leading-none">{ub > 99 ? "99+" : ub}</span>
              )}
            </button>
            );
          })}
          {/* 광고주 전용 — PC 데스크탑 메뉴에 강조 표시 */}
          {isAdvertiser && (
            <button
              onClick={() => setActivePopup("myBannerInquiries")}
              className="shrink-0 ml-2 px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-bold whitespace-nowrap hover:bg-rose-600 transition-colors"
              title={t("header.myBannerInquiries") || "내 배너 문의"}
            >
              💬 {t("header.myBannerInquiries") || "내 배너 문의"}
            </button>
          )}
        </div>

        {/* 우측 영역: 로그인/회원가입 + 언어 선택 — absolute 제거, flex 자식으로 자연 분리 */}
        <div className="hidden md:flex shrink-0 items-center gap-1 pl-4">
          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              <div className="relative">
                <button data-notif-bell onClick={() => { if (typeof Notification !== "undefined" && Notification.permission !== "granted") { window.dispatchEvent(new Event("open-notif-permission")); return; } setNotifOpen(p => !p); if (typeof Notification !== "undefined" && Notification.permission === "granted") registerFcmToken(); }} className="text-base relative">
                  🔔
                  {/* 좌측: 게시글(알림) 수, 우측: 댓글(1:1문의) 수 — 합산 대신 분리 표시 */}
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -left-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white" title={t("notif.new_posts")}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                  {inquiryUnread > 0 && (
                    <span className="absolute -top-1 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-400 px-1 text-[10px] font-bold text-white" title={t("notif.new_comments")}>
                      {inquiryUnread > 99 ? "99+" : inquiryUnread}
                    </span>
                  )}
                </button>
              </div>
              <div data-user-menu className="relative">
                <button
                  onClick={() => setUserMenuOpen((p) => !p)}
                  className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-sky-600 transition-all hover:bg-white/10"
                >
                  {loggedInNick}
                  <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 z-50 w-44 overflow-hidden rounded-xl border border-white/10 bg-slate-900 shadow-xl"
                    style={{ transformOrigin: "top", animation: "dropdownOpen 0.25s ease-out forwards" }}
                  >
                    <button
                      onClick={() => { setUserMenuOpen(false); setActivePopup("profile"); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <UserIcon className="h-4 w-4 text-slate-400" />
                      {t("header.profile")}
                    </button>
                    {isAdvertiser && (
                      <>
                        <div className="border-t border-white/5" />
                        <button
                          onClick={() => { setUserMenuOpen(false); setActivePopup("myBannerInquiries"); }}
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-rose-300 transition-colors hover:bg-rose-500/10 hover:text-rose-200"
                        >
                          <span className="text-base leading-none">💬</span>
                          {t("header.myBannerInquiries") || "내 배너 문의"}
                        </button>
                      </>
                    )}
                    <div className="border-t border-white/5" />
                    <button
                      onClick={() => { setUserMenuOpen(false); setActivePopup("changePassword"); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <KeyRound className="h-4 w-4 text-slate-400" />
                      {t("header.changePassword")}
                    </button>
                    <div className="border-t border-white/5" />
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-sky-400 transition-colors hover:bg-white/5 hover:text-sky-300"
                    >
                      <LogOut className="h-4 w-4 text-sky-400" />
                      {t("header.logout")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={() => setActivePopup("login")}
                className="glow-yellow font-semibold"
              >
                {t("header.login")}
              </button>
              <button
                onClick={() => setActivePopup("signup")}
                className="glow-yellow font-semibold"
              >
                {t("header.signup")}
              </button>
            </>
          )}

          {/* 언어 선택 */}
          <div ref={langRefDesktop} className="relative">
            <button
              onClick={() => {
                if (langOpen) closeLang();
                else setLangOpen(true);
              }}
              className="glow-yellow flex items-center gap-1.5"
            >
              <span>{currentLang.flag}</span>
              <span>{LANG_EN[currentLang.key] || currentLang.label}</span>
            </button>
            {langOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-50 overflow-hidden rounded-lg border border-white/10 bg-slate-900 shadow-xl"
                style={{
                  transformOrigin: "top",
                  animation: langClosing
                    ? "dropdownClose 0.35s ease-in forwards"
                    : "dropdownOpen 0.25s ease-out forwards",
                }}
              >
                {languages.map((l) => (
                  <button
                    key={l.key}
                    onClick={() => {
                      changeLang(l.key);
                      closeLang();
                    }}
                    className={`flex w-full items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                      i18n.language === l.key
                        ? "bg-yellow-400/10 text-yellow-400"
                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <span>{l.flag}</span>
                    <span>{LANG_EN[l.key] || l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* PWA 설치 (Dock에 추가) */}
          {!isStandalone && (
            <button onClick={handlePwaInstall} className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/40 hover:text-white transition-all shrink-0" title={t("install.btn")}>
              <Download className="h-3.5 w-3.5" />
            </button>
          )}

          {/* 데스크탑 새로고침 버튼 제거 — 로고 클릭이 강력 새로고침 (요구사항 #254 #0/#2) */}
        </div>
      </div>

      {/* 모바일 햄버거 backdrop — #286 재요청 후속: portal 로 body 에 mount
          Header 의 stacking context 영향 없이 viewport 전체 cover 보장 */}
      {mobileMenuOpen && createPortal(
        <div
          className="md:hidden fixed inset-0 z-[9990]"
          style={{ touchAction: "none" }}
          onPointerDown={(e) => { e.stopPropagation(); markIgnoreUntil(800); setMobileMenuOpen(false); }}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); markIgnoreUntil(800); setMobileMenuOpen(false); }}
          onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); markIgnoreUntil(800); setMobileMenuOpen(false); }}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          aria-hidden
        />,
        document.body
      )}
      {mobileMenuOpen && createPortal(
        <div className="md:hidden fixed inset-0 z-[9991] pointer-events-none">
          <div
            className="absolute rounded-lg border border-slate-300 bg-white shadow-xl pointer-events-auto overflow-hidden"
            style={isLoggedIn
              ? { top: menuPos.top, left: 8, width: "calc(100vw - 16px)" }   // 로그인: 원래 메인메뉴(전폭 그리드)
              : { top: menuPos.top, left: menuPos.left, minWidth: 180 }}      // 비로그인: 로그인/회원가입 컴팩트
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {!isLoggedIn ? (
              /* 비로그인 = 로그인/회원가입 세로 (아이콘 + 텍스트) */
              <div className="flex flex-col p-1.5">
                <button
                  onClick={() => { setMobileMenuOpen(false); setActivePopup("login"); }}
                  className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold text-sky-600 hover:bg-sky-50 transition-colors"
                >
                  <LogIn className="h-5 w-5 shrink-0" />
                  {t("header.login")}
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); setActivePopup("signup"); }}
                  className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold text-sky-600 hover:bg-sky-50 transition-colors"
                >
                  <UserPlus className="h-5 w-5 shrink-0" />
                  {t("header.signup")}
                </button>
              </div>
            ) : (
              /* 로그인 = 원래 메인메뉴 그리드 (로그아웃은 우상단 닉네임 드롭다운에 있음) */
              <div className="grid grid-cols-5 gap-1.5 p-2">
                {menuItems.map((item) => {
                  const ub = menuBadgeCount(item.url);
                  return (
                    <button
                      key={item.key}
                      onClick={() => { dispatchMenuClick(item); setMobileMenuOpen(false); }}
                      className="relative w-full flex flex-col items-center justify-center gap-0 rounded-lg p-1 text-sky-600 hover:bg-slate-50 transition-colors"
                    >
                      {item.icon_url && (
                        <img src={item.icon_url} alt="" className="h-9 w-9 block mx-auto object-contain" />
                      )}
                      <span className="block w-full text-[12px] leading-none text-center break-keep -mt-0.5 font-semibold">
                        {item.label}
                      </span>
                      {ub > 0 && (
                        <span className="absolute top-0.5 right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">{ub > 99 ? "99+" : ub}</span>
                      )}
                    </button>
                  );
                })}
                {isAdvertiser && (
                  <button
                    onClick={() => { setMobileMenuOpen(false); setActivePopup("myBannerInquiries"); }}
                    className="w-full flex flex-col items-center justify-center gap-0 rounded-lg p-1 text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <span className="h-9 w-9 flex items-center justify-center text-2xl">💬</span>
                    <span className="block w-full text-[12px] leading-none text-center break-keep -mt-0.5 font-semibold">
                      {t("header.myBannerInquiries") || "내 배너 문의"}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </header>
    {notifOpen && <NotificationDropdown onClose={() => setNotifOpen(false)} onOpenPopup={(key) => setActivePopup(key as PopupKey)} />}
    <Suspense fallback={null}>
    {activePopup === "event" && <BannerCollectionPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "favorites" && <BannerFavoritesPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "login" && <LoginPopup onClose={() => {
      const loggedIn = isLoggedInFn();
      setIsLoggedIn(loggedIn);
      setLoggedInNick(localStorage.getItem("nick_name") || "");
      // 알림 클릭 → 비로그인이라 이 LoginPopup 으로 유도됐던 경우, 로그인 성공 시
      // 원래 열려던 팝업(+게시글)으로 자동 복귀. 취소/실패(loggedIn=false)면 그냥 닫는다.
      const pending = pendingNotifTargetRef.current;
      pendingNotifTargetRef.current = null;
      if (pending && loggedIn) {
        setActivePopup(pending.key);
        if (pending.targetId && (pending.key === "improvementRequest" || pending.key === "bugReport")) {
          setAutoOpenBoardItemId(pending.targetId);
        }
      } else {
        setActivePopup(null);
      }
      window.dispatchEvent(new CustomEvent("auth-changed"));
    }} onSwitchToSignup={() => setActivePopup("signup")} />}
    {activePopup === "signup" && <SignupPopup onClose={() => setActivePopup(null)} onSwitchToLogin={() => setActivePopup("login")} />}
    {activePopup === "notice" && <NoticePopup onClose={() => setActivePopup(null)} />}
    {activePopup === "adInquiry" && <AdInquiryPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "improvementRequest" && <UserBoardPopup boardType="IMPROVEMENT_REQUEST" titleKey="header.improvementRequest" autoOpenId={autoOpenBoardItemId} onClose={() => { setActivePopup(null); setAutoOpenBoardItemId(undefined); }} />}
    {activePopup === "bugReport" && <UserBoardPopup boardType="BUG_REPORT" titleKey="header.bugReport" autoOpenId={autoOpenBoardItemId} onClose={() => { setActivePopup(null); setAutoOpenBoardItemId(undefined); }} />}
    {activePopup === "customerInquiry" && <UserBoardPopup boardType="CUSTOMER_INQUIRY" titleKey="header.serviceCenter" autoOpenId={autoOpenBoardItemId} onClose={() => { setActivePopup(null); setAutoOpenBoardItemId(undefined); }} />}
    {activePopup === "travelInfo" && <TravelInfoPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "serviceCenter" && <ServiceCenterPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "help" && <HelpPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "changePassword" && <ChangePasswordPopup onClose={() => setActivePopup(null)} />}
    {activePopup === "myBannerInquiries" && <MyBannerInquiriesPage onClose={() => setActivePopup(null)} />}
    {activePopup === "profile" && (
      <ProfilePopup
        onClose={() => { setActivePopup(null); setProfileHighlightAdvertiser(false); }}
        highlightAdvertiser={profileHighlightAdvertiser}
      />
    )}
    </Suspense>

    {/* PWA 설치 안내 모달 — beforeinstallprompt 미지원 환경 (iOS Safari 등) */}
    {showPwaHelp && (
      <div ref={pwaHelpRef} className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setShowPwaHelp(false)}>
        <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-slate-900 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-amber-400" />
            <h2 className="text-base font-bold text-white">{t("install.title")}</h2>
          </div>
          {isIOS ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">{t("install.iosDesc")}</p>
              <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2">
                <span className="text-xs text-slate-400 w-6 text-center">1.</span>
                <Share className="h-4 w-4 text-sky-400" />
                <span className="text-xs text-slate-300">{t("install.iosStep1")}</span>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2">
                <span className="text-xs text-slate-400 w-6 text-center">2.</span>
                <Plus className="h-4 w-4 text-sky-400" />
                <span className="text-xs text-slate-300">{t("install.iosStep2")}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-300">{t("install.manualDesc")}</p>
              <p className="text-xs text-slate-400">{t("install.manualHint")}</p>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <button
              onClick={() => setShowPwaHelp(false)}
              className="rounded-lg bg-amber-500 px-6 py-2 text-sm font-bold text-black hover:bg-amber-400 transition-colors"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
